import { all, takeLatest, call, put } from 'redux-saga/effects'
import { toast } from 'react-toastify'

import { PATHS } from 'consts/paths'
import { inviteStatus } from 'consts/types'
import apiEndPoints from 'consts/apiEndPoints'
import api from 'services/api'
import history from 'services/history'
import handleErrors from 'services/handleErrors'

import { confirmationDialog } from 'components/ConfirmationDialog'
import { configAuthorization } from '../../auth/sagas'
import { signInSuccess } from '../../auth/reducer'
import { setUser } from '../../user/reducer'
import {
  getCompaniesRequest,
  getCompaniesSuccess,
  getCompaniesFailure,
  createCompanyRequest,
  updateCompanyRequest,
  removeCompanyRequest,
  removeCompanySuccess,
  handleSaveCompany,
  errorOnSave,
  closeFormEdit,
  sendInviteRequest,
  sendInviteSuccess,
  sendInviteCancel,
  logIsAsCompanyRequest,
  logIsAsCompanyFinish,
} from './reducer'

function* getCompanies({ payload = {} }) {
  try {
    const {
      page,
      perPage,
      order,
      orderAsc,
      type,
      status,
      inviteStatus,
      query,
    } = payload

    const response = yield call(api.get, apiEndPoints.webCompanies.root, {
      params: {
        page,
        per_page: perPage,
        order,
        order_asc: orderAsc,
        //
        type: ['', 'all'].includes(type) ? undefined : type,
        status: ['', 'all'].includes(status) ? undefined : status,
        invite_status: ['', 'all'].includes(inviteStatus)
          ? undefined
          : inviteStatus,
        //
        q: query,
      },
    })

    const webCompanies = response.data
    const webCompaniesCount = response.headers['total']

    yield put(getCompaniesSuccess({ webCompanies, webCompaniesCount }))
  } catch (error) {
    handleErrors(error, 'Não foi possível buscar as empresas')
    yield put(getCompaniesFailure())
  }
}

function* createCompany({ payload: webCompany }) {
  try {
    const requestBody = {
      wee_tipo: webCompany.wee_tipo,
      cod_empresa: webCompany.cod_empresa,
      wee_email: webCompany.wee_email,
      wee_razao_social: webCompany.wee_razao_social,
      wee_telefone: webCompany.wee_telefone,
      dsc_empresa: webCompany.dsc_empresa,
      emp_cidade: webCompany.emp_cidade,
      dsc_uf: webCompany.dsc_uf,
      wee_ativo: webCompany.wee_ativo,
    }

    yield call(api.post, apiEndPoints.webCompanies.root, requestBody)

    toast.success('Empresa cadastrada com sucesso')

    // -------------------------------

    yield put(closeFormEdit())
    yield put(getCompaniesRequest())
  } catch (error) {
    handleErrors(error, 'Não foi possível criar a empresa')
    yield put(errorOnSave())
  }
}

function* updateCompany({ payload }) {
  try {
    const { formValues, webCompany } = payload
    const emailChanged = formValues.wee_email !== webCompany.wee_email

    const alreadyInvited =
      webCompany.admin_web_user.invite_status !== inviteStatus.NOT_INVITED

    if (alreadyInvited && emailChanged) {
      const confirm = yield call(
        confirmationDialog.open,
        'Atualizar e-mail ?',
        'Caso o e-mail seja alterado, um novo convite será enviado para a empresa. O acesso ficará suspenso até a validação do novo convite. Deseja continuar ?'
      )

      confirmationDialog.close()

      if (!confirm) {
        yield put(errorOnSave())
        return
      }
    }

    yield call(
      api.put,
      apiEndPoints.webCompanies.onMember(webCompany.id),
      formValues
    )
    toast.success('Empresa atualizada com sucesso')

    // -------------------------------

    yield put(closeFormEdit())
    yield put(getCompaniesRequest())
  } catch (error) {
    handleErrors(error, 'Não foi possível atualizar o usuário')
    yield put(errorOnSave())
  }
}

function* removeCompany({ payload: webCompany }) {
  try {
    const webUser = webCompany.admin_web_user
    const confirm = yield call(
      confirmationDialog.open,
      'Remover Empresa',
      `Tem certeza que deseja remover a empresa ${webUser.weu_nome}`
    )

    confirmationDialog.close()

    if (!confirm) {
      yield put(errorOnSave())
      return
    }

    // -------------------------------

    yield call(api.delete, apiEndPoints.webCompanies.onMember(webCompany.id))
    yield put(removeCompanySuccess(webCompany))

    toast.success('Empresa removida com sucesso')
  } catch (error) {
    handleErrors(error, 'Não foi possível remover a empresa')
    yield put(errorOnSave())
  }
}

function* logIsAsCompany({ payload: webCompany }) {
  if (!webCompany.admin_web_user) return

  try {
    const response = yield call(api.post, apiEndPoints.webUsers.changeUser, {
      weu_id: webCompany.admin_web_user.id,
    })

    const token = response.data.auth_token
    const profile = response.data.user

    configAuthorization(token)

    yield put(signInSuccess({ token }))
    yield put(setUser(profile))

    history.push(
      PATHS.defaultPath(profile.web_company.wee_tipo, profile.weu_tipo)
    )
  } catch (error) {
    handleErrors(error, 'Não foi possível completar o acesso [empresa]')
  } finally {
    yield put(logIsAsCompanyFinish())
  }
}

function* sendInvite({ payload: webCompany }) {
  const webUser = webCompany.admin_web_user
  try {
    const confirm = yield call(
      confirmationDialog.open,
      'Enviar Convite',
      `Enviar convite para a empresa ${webUser.weu_email}`
    )
    confirmationDialog.close()

    if (!confirm) {
      yield put(sendInviteCancel())
      return
    }

    const response = yield call(api.post, apiEndPoints.invites.root, {
      user_id: webUser.id,
    })
    const recipient = response.data.weu_id

    yield put(sendInviteSuccess(recipient))
    toast.success('Convite enviado com sucesso')
  } catch (error) {
    yield put(sendInviteCancel())
    handleErrors(error, 'Não foi possível enviar o convite')
  }
}

// --------------------------------

function* handleSave({ payload }) {
  const isUpdate = !!payload.webCompany.id
  if (isUpdate) yield put(updateCompanyRequest(payload))
  else yield put(createCompanyRequest(payload.formValues))
}

export default all([
  takeLatest(getCompaniesRequest.type, getCompanies),
  takeLatest(handleSaveCompany.type, handleSave),
  takeLatest(createCompanyRequest.type, createCompany),
  takeLatest(updateCompanyRequest.type, updateCompany),
  takeLatest(removeCompanyRequest.type, removeCompany),
  takeLatest(sendInviteRequest.type, sendInvite),
  takeLatest(logIsAsCompanyRequest.type, logIsAsCompany),
])
